import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sileo } from "sileo";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type {
  ApiTableGroup,
  ApiTableGroupCreate,
  ApiTableGroupUpdate,
  PaginatedResponse,
} from "@/types/api";

const TABLE_GROUP_FIELDS = [
  "id",
  "location_id",
  "name",
  "tables",
  "min_capacity",
  "max_capacity",
] as const satisfies readonly (keyof ApiTableGroup)[];

function showApiError(error: unknown): void {
  const data = (error as { data?: { detail?: unknown } }).data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.length > 0) {
    sileo.error({ description: detail });
    return;
  }
  sileo.error({ description: "Error de conexión. Intentá de nuevo." });
}

export function useTableGroups() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["table-groups", organizationId, locationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: TABLE_GROUP_FIELDS.join(",") });
      const res = await api.get(`table-groups/?${params}`).json<PaginatedResponse<ApiTableGroup>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

export function useCreateTableGroup() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (data: ApiTableGroupCreate) =>
      api.post("table-groups/", { json: data }).json<ApiTableGroup>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["table-groups", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["table-groups", organizationId, locationId] });
    },
  });
}

export function useUpdateTableGroup() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: ApiTableGroupUpdate }) =>
      api.patch(`table-groups/${id}/`, { json: changes }).json<ApiTableGroup>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["table-groups", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["table-groups", organizationId, locationId] });
    },
  });
}

export function useDeleteTableGroup() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (id: string) => api.delete(`table-groups/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["table-groups", organizationId, locationId] });
    },
    onError: (error) => {
      showApiError(error);
      queryClient.invalidateQueries({ queryKey: ["table-groups", organizationId, locationId] });
    },
  });
}
